import React from "react";
import { create } from "zustand";
import { Project } from "@/models/project";
import { normalizeProjectIdentity, normalizeProjectModel } from "@/utils/projectModelNormalizer";

// NOTE: normalizeProcessCollection is intentionally NOT imported here.
// It must only run at load time (setCurrentProject) and save time (saveCurrentProject),
// never during in-place edits — see updatePart fix below.

interface ChatMessage {
  id: string;
  content: string;
  sender: string;
  isNew?: boolean;
}

interface CodicentState {
  projects: Project[];
  currentProject: Project | null;
  chatMessages: ChatMessage[];
  showWelcome: boolean;

  setProjects: (projects: Project[]) => void;
  setCurrentProject: (project: Project) => void;
  updateCurrentProject: (path: string[], value: any) => void;
  updatePart: (path: string[], value: any) => void;
  saveCurrentProject: () => Promise<boolean>;
  setChatMessages: (messages: ChatMessage[]) => void;
  addChatMessage: (message: ChatMessage) => void;
  clearChatMessages: () => void;
  setShowWelcome: (show: boolean) => void;

  isChatOpen: boolean;
  setChatOpen: React.Dispatch<React.SetStateAction<boolean>>;

  previousSidebarOpen: boolean | undefined;
  setPreviousSidebarOpen: (isOpen: boolean | undefined) => void;
  isExportOpen: boolean;
  setIsExportOpen: (value: boolean) => void;
}

export const useCodicentStore = create<CodicentState>((set, get) => ({
  projects: [], // Not used yet. Still using projects from useReqtangleCodicent.ts
  currentProject: null,
  chatMessages: [],
  showWelcome: true,
  isChatOpen: false,

  setChatOpen: (value) => set({ isChatOpen: typeof value === 'function' ? value(get().isChatOpen) : value }),

  // Set list of available projects
  setProjects: (projects) => {
    const normalizedProjects = projects.map((project) => normalizeProjectIdentity(project, project.id) as Project);
    localStorage.setItem("codicentProjects", JSON.stringify(normalizedProjects));
    set({ projects: normalizedProjects });
  },

  // Set selected/current project
  setCurrentProject: (project) => {
    const normalizedProject = normalizeProjectModel(project, project.id);
    localStorage.setItem("codicentCurrentProject", JSON.stringify(normalizedProject));
    set({ currentProject: normalizedProject });
  },

  previousSidebarOpen: undefined,
  setPreviousSidebarOpen: (isOpen) => set({ previousSidebarOpen: isOpen }),

  isExportOpen: false,
  setIsExportOpen: (value: boolean) => set({ isExportOpen: value }),

  // Update zustand store state
  updateCurrentProject: (path, value) => {
    const current = structuredClone(get().currentProject);
    if (!current) return;

    // Handle empty path - replace the entire project
    if (path.length === 0) {
      localStorage.setItem("codicentCurrentProject", JSON.stringify(value));
      set({ currentProject: value });
      return;
    }

    // TypeScript does not allow indexing Project with string, so we cast to any for dynamic path traversal
    let obj: any = current;
    for (let i = 0; i < path.length - 1; i++) {
      obj = obj[path[i]] ??= {};
    }
    obj[path[path.length - 1]] = value;

    localStorage.setItem("codicentCurrentProject", JSON.stringify(current));
    set({ currentProject: current });
  },

  // Update part of zustand store state - with the help of a path leading to the right part of the json tree
  updatePart: (path, value) => {
    const current = structuredClone(get().currentProject);
    if (!current) return;

    // Helper to resolve ID-based path segments like 'processes[id=1]'
    const resolveIdSelector = (obj: any, segment: string | number): any => {
      const strSegment = String(segment);
      const match = strSegment.match(/^(\w+)\[id=([^\]]+)\]$/);
      if (!match) return null;

      const [_, prop, id] = match;
      const items = obj?.[prop];
      if (!Array.isArray(items)) return null;

      return items.find((item: any) => String(item?.id) === String(id)) ?? null;
    };

    // Guard: ensure newly added stages have IDs (non-destructive — skips items that already have an ID)
    const isUpdatingStages = path.some(seg => String(seg).includes('stages[')) ||
      (path.includes("stages") && Array.isArray(value));

    if (isUpdatingStages && Array.isArray(value)) {
      value = value.map((stage: any, index: number) => {
        if (!stage.id || stage.id === null || stage.id === '') {
          const newId = `${Date.now()}-stage${index + 1}`;
          console.log(`Generated missing stage ID: ${newId} for stage "${stage.name || 'unnamed'}"`);
          return { ...stage, id: newId };
        }
        return stage;
      });
    }

    // Guard: ensure newly added tasks have IDs (non-destructive — skips items that already have an ID)
    const isUpdatingTasks = path.some(seg => String(seg).includes('tasks[')) ||
      (path.includes("tasks") && Array.isArray(value));

    if (isUpdatingTasks && Array.isArray(value)) {
      value = value.map((task: any, index: number) => {
        if (!task.id || task.id === null || task.id === '') {
          const newId = `${Date.now()}-${index + 1}`;
          console.log(`Generated missing task ID: ${newId} for task "${task.name || 'unnamed'}"`);
          return { ...task, id: newId };
        }
        return task; // existing ID preserved, no reordering
      });
    }

    // Handle the actual path traversal and value setting
    let obj: any = current;
    const lastIndex = path.length - 1;

    // Traverse all but the last segment
    for (let i = 0; i < lastIndex; i++) {
      const segment = path[i];
      const strSegment = String(segment);

      // Handle ID-based selectors like 'processes[id=1]'
      if (strSegment.includes('[') && strSegment.includes(']')) {
        const nextObj = resolveIdSelector(obj, segment);
        if (nextObj === null) {
          console.warn(`Could not resolve path segment: ${segment}`, { obj, path });
          return;
        }
        obj = nextObj;
      } else {
        // Handle regular path segments
        if (obj[segment] === undefined) {
          // Create nested objects as needed
          obj[segment] = {};
        }
        obj = obj[segment];
      }
    }

    // Handle the last segment (where we set the value)
    const lastSegment = path[lastIndex];
    if (typeof lastSegment === 'string' && lastSegment.includes('[') && lastSegment.includes(']')) {
      // Handle ID-based selector for the last segment
      const match = lastSegment.match(/^(\w+)\[id=([^\]]+)\]$/);
      if (match) {
        const [_, prop, id] = match;
        const items = obj?.[prop];
        if (Array.isArray(items)) {
          const index = items.findIndex((item: any) => String(item?.id) === String(id));
          if (index !== -1) {
            items[index] = value;
          } else {
            // If not found, add to the array
            items.push(value);
          }
          // Update the reference
          obj[prop] = [...items];
        }
      }
    } else {
      // Regular path assignment
      obj[lastSegment] = value;
    }

    localStorage.setItem("codicentCurrentProject", JSON.stringify(current));
    set({ currentProject: current });
  },

  // Save project from zustand store state to backend state (codicent)
  saveCurrentProject: async () => {
    const current = get().currentProject;
    if (!current) throw new Error("No current project to save");
    if (!window.Codicent) throw new Error("window.Codicent is not available");
    try {
      const normalizedProject = normalizeProjectModel(current, current.id);
      const json = JSON.stringify(normalizedProject);
      localStorage.setItem("codicentCurrentProject", json);
      set({ currentProject: normalizedProject });
      await window.Codicent.postMessage({
        message: `@${normalizedProject.codicent} #reqtangle-project-json\n${json}`,
        type: "info",
      });
      return true;
    } catch (error) {
      console.error("Error saving project:", error);
      throw error;
    }
  },

  setChatMessages: (messages) => {
    localStorage.setItem("codicentChatMessages", JSON.stringify(messages));
    set({ chatMessages: messages });
  },

  addChatMessage: (message) => {
    const messages = [...get().chatMessages, message];
    localStorage.setItem("codicentChatMessages", JSON.stringify(messages));
    set({ chatMessages: messages });
  },

  clearChatMessages: () => {
    localStorage.removeItem("codicentChatMessages");
    set({ chatMessages: [] });
  },

  setShowWelcome: (show) => set({ showWelcome: show }),
}));